/** Frontend-API für den Page-Copilot (Opus). Erste Seite: Goal-Tree (Ziele). */

import type { GoalNode } from './goal-tree-api.js';

export interface CopilotOp {
  op: 'create_vision' | 'create_pillar' | 'create_objective' | 'update_node' | 'delete_node';
  id?: number;
  text?: string;
  weight?: number;
  parentText?: string;
  parentId?: number;
  hardConstraints?: Record<string, unknown>;
  softSignals?: Record<string, unknown>;
}

export interface GoalTreeAssistResponse {
  reply: string;
  applied: Array<{ op: CopilotOp; node?: GoalNode }>;
  pending: CopilotOp[];
  errors: string[];
  nodes: GoalNode[];
}

async function postJson<T>(url: string, body: unknown): Promise<T> {
  const res = await fetch(url, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(body),
  });
  if (!res.ok) {
    const b = await res.json().catch(() => ({ error: res.statusText }));
    throw new Error(b.error?.message ?? b.error ?? `Request failed: ${res.status}`);
  }
  return res.json() as Promise<T>;
}

export async function goalTreeAssist(companyId: number, message: string): Promise<GoalTreeAssistResponse> {
  return postJson<GoalTreeAssistResponse>('/api/copilot/goal-tree', { companyId, message });
}

export async function applyGoalTreeOp(companyId: number, operation: CopilotOp): Promise<{ ok: boolean; nodes: GoalNode[] }> {
  return postJson<{ ok: boolean; nodes: GoalNode[] }>('/api/copilot/goal-tree/apply', { companyId, operation });
}

// --- Generische Copilot-Antwort (Signale / Decisions / Freigaben) ---

export interface ApprovalOp { op: 'approve' | 'veto'; actionId: string; reason?: string }

export interface CopilotResponse {
  reply: string;
  applied: Array<{ op: { op: string; text?: string }; node?: { text?: string } }>;
  pending: Array<Record<string, unknown>>;
  errors: string[];
}

export async function askSignals(message: string): Promise<CopilotResponse> {
  return postJson<CopilotResponse>('/api/copilot/signals', { message });
}

export async function askDecisions(message: string): Promise<CopilotResponse> {
  return postJson<CopilotResponse>('/api/copilot/decisions', { message });
}

export async function askApprovals(message: string): Promise<CopilotResponse> {
  return postJson<CopilotResponse>('/api/copilot/approvals', { message });
}

export async function applyApprovalOp(operation: ApprovalOp): Promise<{ ok: boolean }> {
  return postJson<{ ok: boolean }>('/api/copilot/approvals/apply', { operation });
}
